export type SupplierCertStatus = 'valid' | 'expiring' | 'expired' | 'none';

export type SupplierStatus =
  | 'prospect'
  | 'onboarding'
  | 'approved'
  | 'active'
  | 'probation'
  | 'suspended'
  | 'terminated';

export interface SupplierReadiness {
  ready: boolean;
  gaps: number;
}

export interface SupplierLocationView {
  locationId: string;
  address: string;
  postcode: string;
  country: string;
}

export interface SupplierSkuLinkView {
  inventorySkuId: string | null;
  skuCode: string;
  productName: string;
  supplierSkuCode: string;
  leadTimeMeanDays: number | null;
  leadTimeStdDays: number | null;
  minimumOrderQuantity: number | null;
  orderMultiple: number | null;
  orderMultipleUnit: string;
  incoterms: string;
  isPreferred: boolean | null;
  status: string;
}

export interface Supplier {
  id: string;
  supplierId: string;
  name: string;
  type: string;
  contactName: string;
  contactEmail: string;
  contactNumber: string;
  hqId: string;
  hqName: string;
  locations: SupplierLocationView[];
  suppliedSkus: SupplierSkuLinkView[];
  sourceType: string;
  brands: string[];
  coverage: string[];
  certStatus: SupplierCertStatus;
  certLabel: string | null;
  certExpiry: string | null;
  readiness: SupplierReadiness | null;
  status: SupplierStatus;
  isArchived: boolean;
}

export const SOURCE_TYPES = [
  'internal production site',
  'contract manufacturer',
  'external supplier',
  'co-packer',
];

export const sourceTypeLabelOf = (sourceType: string): string => {
  switch (sourceType) {
    case 'internal production site':
      return 'Internal production';
    case 'contract manufacturer':
      return 'Contract manufacturer';
    case 'external supplier':
      return 'External supplier';
    case 'co-packer':
      return 'Co-packer';
    default:
      return sourceType || 'Unspecified';
  }
};

export const sourceTypeBadgeIntentOf = (
  sourceType: string
): 'info' | 'neutral' =>
  sourceType === 'internal production site' ? 'info' : 'neutral';

export const certBadgeIntentOf = (
  status: SupplierCertStatus
): 'success' | 'warning' | 'danger' | 'neutral' => {
  switch (status) {
    case 'valid':
      return 'success';
    case 'expiring':
      return 'warning';
    case 'expired':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const certLabelOf = (supplier: Supplier): string => {
  if (supplier.certStatus === 'none' || !supplier.certLabel) {
    return 'No certification';
  }
  if (supplier.certStatus === 'expired') {
    return `${supplier.certLabel} · expired`;
  }
  return supplier.certExpiry
    ? `${supplier.certLabel} · exp ${supplier.certExpiry}`
    : supplier.certLabel;
};

export const statusBadgeIntentOf = (
  status: SupplierStatus
): 'info' | 'success' | 'warning' | 'danger' | 'neutral' => {
  switch (status) {
    case 'active':
      return 'info';
    case 'approved':
      return 'success';
    case 'onboarding':
    case 'prospect':
      return 'warning';
    case 'probation':
    case 'suspended':
    case 'terminated':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const statusLabelOf = (status: SupplierStatus): string =>
  status.charAt(0).toUpperCase() + status.slice(1);
